import { CoObjectTypeEnum, SourceEnum, TgkAmount, TgkTable } from "../../api/models";
import {
  ComponentReference,
  CoObjectGeneralInfo,
  CoObjectItem,
  DimensionDetails,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../components/interfaces/CoObject";
import {
  getTgkAmountByLogicId,
  getTgkDimensionAttributeByLogicId,
  getTgkTableByLogicId,
  getTgkTextByLogicId,
} from "../../api/object/CoObjectGeneral";
import { AdvancedDocumentMetadata } from "./DocumentAdvancedCustomXmlHelper";
import { TgkDimension } from "../../api/models/tgk-dimension";
import { TgkTableParameter } from "../../api/models/tgk-table-parameter";
import { FormatMetadata } from "../../api/models/format-metadata";
import { TgkDimensionAttribute } from "../../api/models/tgk-dimension-attribute";
import { TgkText } from "../../api/models/tgk-text";
import { ObjectPanelValidator } from "../../components/coobjects/co-panel/ObjectPanelComponent";
import { ReferringScenariosEnum } from "../../api/models/ReferringScenarios";

export const inheritComponentData = async (coObject: CoObjectItem): Promise<CoObjectItem> => {
  let inheritedCoObject: CoObjectItem;
  switch (coObject.info.objectType) {
    case "AMOUNT":
      inheritedCoObject = await inheritAmountComponentData(coObject, coObject.info.componentReference);
      break;
    case "DIMENSION_ATTRIBUTE":
      inheritedCoObject = await inheritDimensionAttributeComponentData(coObject, coObject.info.componentReference);
      break;
    case "TABLE":
    case "CHART":
      inheritedCoObject = await inheritTableOrChartComponentData(coObject, coObject.info.componentReference);
      break;
    case "TGK_TEXT":
      inheritedCoObject = await inheritTgkTextComponentData(coObject, coObject.info.componentReference);
      break;
    default:
      // eslint-disable-next-line no-undef
      console.log("Unexpected component type", coObject.info.objectType);
  }

  return inheritedCoObject;
};

export const validateComponentData = (
  coObject: CoObjectItem,
  modifiedFields: boolean,
  modifiedSource: boolean
): ObjectPanelValidator => {
  let isAllValid = true;
  if (!coObject.info) isAllValid = false;
  else {
    if (coObject.info.objectName == null || coObject.info.objectName == "") isAllValid = false;
    // @ts-ignore
    if (coObject.info.objectType == null || coObject.info.objectType == "") isAllValid = false;
  }

  return {
    isAllValid: isAllValid,
    modifiedFields: modifiedFields,
    modifiedSource: modifiedSource,
  };
};

const inheritAmountComponentData = async (
  coObject: CoObjectItem,
  componentReference: ComponentReference
): Promise<CoObjectItem> => {
  let tgkAmount = await getTgkAmountByLogicId(componentReference.componentLogicId);

  if (tgkAmount.type === "AMOUNT_AIH") {
    tgkAmount.type = CoObjectTypeEnum.AMOUNT;
  }

  const formatType =
    tgkAmount?.source === SourceEnum.EXCEL || tgkAmount?.source === SourceEnum.TAGETIK_FORM
      ? tgkAmount.inheritFormat
        ? "DOCUMENT_ROOT"
        : "SOURCE"
      : tgkAmount.formatType ?? "NUMBER";

  return {
    ...coObject,
    info: inheritGeneralInfo(coObject, CoObjectTypeEnum.AMOUNT, componentReference),
    source: inheritAmountSource(coObject, tgkAmount),
    ...(tgkAmount?.source === SourceEnum.TAGETIK_FORM && { parameters: inheritParameters(tgkAmount.parameters) }),
    advanced: inheritAdvancedInfo(coObject, tgkAmount.formatMetadata, formatType, tgkAmount.inheritFormat),
  };
};

const inheritDimensionAttributeComponentData = async (
  coObject: CoObjectItem,
  componentReference: ComponentReference
): Promise<CoObjectItem> => {
  let tgkDimensionAttribute = await getTgkDimensionAttributeByLogicId(componentReference.componentLogicId);

  return {
    ...coObject,
    info: inheritGeneralInfo(coObject, CoObjectTypeEnum.DIMENSION_ATTRIBUTE, componentReference),
    source: inheritDimensionAttributeSource(coObject, tgkDimensionAttribute),
  };
};

const inheritTableOrChartComponentData = async (
  coObject: CoObjectItem,
  componentReference: ComponentReference
): Promise<CoObjectItem> => {
  let tgkTable = await getTgkTableByLogicId(componentReference.componentLogicId);

  return {
    ...coObject,
    info: inheritGeneralInfo(coObject, tgkTable.type, componentReference),
    source: inheritTableOrChartSource(coObject, tgkTable),
    ...(tgkTable?.source !== SourceEnum.EXCEL && { parameters: inheritParameters(tgkTable.parameters) }),
    advanced: inheritAdvancedInfo(
      coObject,
      tgkTable.formatMetadata,
      tgkTable.formatMetadata ? "SOURCE" : "DOCUMENT_ROOT",
      tgkTable.inheritFormat
    ),
  };
};

const inheritTgkTextComponentData = async (
  coObject: CoObjectItem,
  componentReference: ComponentReference
): Promise<CoObjectItem> => {
  let tgkText = await getTgkTextByLogicId(componentReference.componentLogicId);

  return {
    ...coObject,
    info: inheritGeneralInfo(coObject, CoObjectTypeEnum.TGK_TEXT, componentReference),
    source: inheritTgkTextSource(coObject, tgkText),
  };
};

const inheritGeneralInfo = (
  coObject: CoObjectItem,
  objectType: CoObjectTypeEnum,
  componentReference: ComponentReference
): CoObjectGeneralInfo => {
  return {
    ...coObject.info,
    objectType: objectType,
    componentReference: componentReference,
  };
};

const inheritAmountSource = (coObject: CoObjectItem, tgkAmount: TgkAmount): TgkAmountObjectSource => {
  let inheritedSource: TgkAmountObjectSource;
  switch (tgkAmount?.source) {
    case SourceEnum.EXCEL:
      inheritedSource = {
        ...coObject.source,
        type: "AMOUNT",
        objectSource: tgkAmount?.source?.toString(),
        dbApplication: { id: "", desc: "" },
        form: {
          code: tgkAmount.formId,
          oid: tgkAmount.formId,
          desc: tgkAmount.formDesc,
          oidPath: undefined,
        },
        sheet: {
          code: tgkAmount.sheetCode ?? tgkAmount.sheet,
          desc: tgkAmount.sheet,
          uid: "",
        },
        area: {
          name: tgkAmount.area,
        },
        cell: tgkAmount.cell,
      };
      break;
    case SourceEnum.TAGETIK_FORM:
      inheritedSource = {
        ...coObject.source,
        type: "AMOUNT",
        objectSource: tgkAmount?.source?.toString(),
        dbApplication: { id: tgkAmount?.dbId, desc: "" },
        form: {
          code: tgkAmount.formId,
          oid: tgkAmount.formId,
          desc: tgkAmount.formDesc,
          oidPath: undefined,
        },
        sheet: {
          code: tgkAmount.sheetCode ?? tgkAmount.sheet,
          desc: tgkAmount.sheet,
          uid: "",
        },
        area: {
          name: tgkAmount.area,
        },
        cell: tgkAmount.cell,
      };
      break;
    default:
      inheritedSource = {
        ...coObject.source,
        type: tgkAmount?.analyticalWorkspace?.code ? "AMOUNT_AIH" : "AMOUNT",
        objectSource: "FORM",
        dbApplication: { id: tgkAmount?.dbId, desc: "" },
        dimensions: inheritDimensions(tgkAmount?.dimensions),
        analyticalWorkspace: tgkAmount?.analyticalWorkspace,
        dataset: tgkAmount?.dataset,
        amount: tgkAmount?.amount,
      };
      break;
  }

  return inheritedSource;
};

const inheritDimensionAttributeSource = (
  coObject: CoObjectItem,
  tgkDimensionAttribute: TgkDimensionAttribute
): TgkDimensionAttributeSource => {
  return {
    ...coObject.source,
    locale: null,
    type: "DIMENSION_ATTRIBUTE",
    dbApplication: { id: tgkDimensionAttribute?.dbId, desc: "" },
    dimensions: inheritDimensions(tgkDimensionAttribute?.dimensions),
  };
};

const inheritTableOrChartSource = (coObject: CoObjectItem, tgkTable: TgkTable): TgkTableObjectSource => {
  return {
    ...coObject.source,
    type: "TABLE",
    objectSource: tgkTable?.source?.toString(),
    dbApplication: tgkTable?.source !== SourceEnum.EXCEL ? { id: tgkTable.form?.dbId, desc: "" } : { id: "", desc: "" },
    form: {
      code: tgkTable.form.formId,
      oid: tgkTable.form.formId,
      desc: tgkTable.form.formDesc,
      oidPath: mapPathToOidPath(tgkTable.form.path),
    },
    sheet: {
      code: tgkTable.sheetCode,
      desc: tgkTable.sheet,
      uid: "",
    },
    area: {
      name: tgkTable.area,
    },
    elabResultType: tgkTable.elabResultType,
    inheritFormatImage: tgkTable.inheritFormatImage,
    imageExtension: tgkTable.formatImage?.imageExtension,
    imageResolution: tgkTable.formatImage?.imageResolution,
    chart: {
      name: tgkTable.chart,
    },
    path: null,
  };
};

const inheritTgkTextSource = (coObject: CoObjectItem, tgkText: TgkText): TgkTextSource => {
  return {
    ...coObject.source,
    type: "TGK_TEXT",
    dbApplication: { id: tgkText?.dbId, desc: "" },
    analyticalWorkspace: {
      code: tgkText?.analyticalWorkspace?.code,
      description: tgkText?.analyticalWorkspace?.description,
    },
    dataset: {
      code: tgkText?.dataset?.code,
      description: tgkText?.dataset?.description,
      partitionType: tgkText?.dataset?.partitionType,
    },
    richText: {
      description: tgkText?.richText?.description,
      id: tgkText?.richText?.id,
      fieldType: tgkText?.richText?.fieldType,
      textFormatType: tgkText?.richText?.textFormatType,
    },
    process: { code: tgkText?.process?.code, numberOfCycles: tgkText?.process?.numberOfCycles },
    dimensions: inheritDimensions(tgkText?.dimensions),
  };
};

const inheritAdvancedInfo = (
  coObject: CoObjectItem,
  formatMetadata: FormatMetadata,
  formatType: string,
  inheritFormat: boolean
): AdvancedDocumentMetadata => {
  return {
    ...coObject.advanced,
    numberFormat: formatMetadata?.numberFormat?.format,
    typeNumber: formatMetadata?.numberType,
    percentageFormat: formatMetadata?.percentageFormat?.format,
    typePercentage: formatMetadata?.percentageType,
    dateFormat: formatMetadata?.dateFormat?.format,
    typeDate: formatMetadata?.dateType,
    inheritFormat: inheritFormat,
    choice: formatType,
  };
};

export const inheritDimensions = (tgkDimensions: TgkDimension[]): DimensionDetails[] => {
  return tgkDimensions?.map((dim) => {
    const hierarchy = isTgkDimensionHierarchyComplete(dim)
      ? {
          oid: dim.hierarchy.oid,
          desc: dim.hierarchy.desc,
          code: dim.hierarchy.code,
        }
      : undefined;
    return {
      dimensionOid: dim?.dimensionOid,
      hierarchy,
      values: dim?.values.map((val) => {
        return {
          oidPath: val?.oidPath,
          oid: val?.oid,
          desc: val?.desc,
          code: val?.code,
          ref: val?.ref as ReferringScenariosEnum,
        };
      }),
      attributeCode: dim?.attributeCode,
      label: dim?.label,
      type: dim?.type,
      inherited: dim?.inherited,
    };
  });
};

export const inheritParameters = (tgkTableParameters: TgkTableParameter[]): DimensionDetails[] => {
  return tgkTableParameters?.map((param) => {
    const hierarchy = isTgkTableParameterHierarchyComplete(param)
      ? {
          oid: param.hierarchy.oid,
          desc: param.hierarchy.desc,
          code: param.hierarchy.code,
        }
      : undefined;
    return {
      dimensionOid: param?.dimensionOid,
      hierarchy,
      values: param?.values.map((val) => {
        return {
          oidPath: val?.oidPath,
          oid: val?.oid,
          desc: val?.desc,
          code: val?.code,
          ref: val?.ref as ReferringScenariosEnum,
        };
      }),
      structureCode: param?.structureCode,
      code: param?.code,
      inherited: param?.inherited,
    };
  });
};

const isTgkDimensionHierarchyComplete = (dimension: TgkDimension): boolean => {
  return !!(dimension.hierarchy && dimension.hierarchy.oid && dimension.hierarchy.desc);
};

const isTgkTableParameterHierarchyComplete = (parameter: TgkTableParameter): boolean => {
  return !!(parameter.hierarchy && parameter.hierarchy.oid && parameter.hierarchy.desc);
};

const mapPathToOidPath = (input: string | undefined | null): string[] => {
  if (!input) {
    return [];
  }
  return input.split(",");
};
